/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace ns
*/
var ns = {};

/**
* @name cumax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/cumax}
*/
import cumax from './../../base/cumax';
setReadOnly( ns, 'cumax', cumax );

/**
* @name cumaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/cumaxabs}
*/
import cumaxabs from './../../base/cumaxabs';
setReadOnly( ns, 'cumaxabs', cumaxabs );

/**
* @name cumin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/cumin}
*/
import cumin from './../../base/cumin';
setReadOnly( ns, 'cumin', cumin );

/**
* @name cuminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/cuminabs}
*/
import cuminabs from './../../base/cuminabs';
setReadOnly( ns, 'cuminabs', cuminabs );

/**
* @name dists
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists}
*/
import dists from './../../base/dists';
setReadOnly( ns, 'dists', dists );

/**
* @name ndarray
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/ndarray}
*/
import ndarray from './../../base/ndarray';
setReadOnly( ns, 'ndarray', ndarray );

/**
* @name sdsnanmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/sdsnanmean}
*/
import sdsnanmean from './../../base/sdsnanmean';
setReadOnly( ns, 'sdsnanmean', sdsnanmean );

/**
* @name snanstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/snanstdev}
*/
import snanstdev from './../../base/snanstdev';
setReadOnly( ns, 'snanstdev', snanstdev );

/**
* @name snanstdevch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/snanstdevch}
*/
import snanstdevch from './../../base/snanstdevch';
setReadOnly( ns, 'snanstdevch', snanstdevch );

/**
* @name snanstdevpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/snanstdevpn}
*/
import snanstdevpn from './../../base/snanstdevpn';
setReadOnly( ns, 'snanstdevpn', snanstdevpn );

/**
* @name snanstdevtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/snanstdevtk}
*/
import snanstdevtk from './../../base/snanstdevtk';
setReadOnly( ns, 'snanstdevtk', snanstdevtk );

/**
* @name snanstdevwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/snanstdevwd}
*/
import snanstdevwd from './../../base/snanstdevwd';
setReadOnly( ns, 'snanstdevwd', snanstdevwd );

/**
* @name snanstdevyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/snanstdevyc}
*/
import snanstdevyc from './../../base/snanstdevyc';
setReadOnly( ns, 'snanstdevyc', snanstdevyc );

/**
* @name snanvariance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/snanvariance}
*/
import snanvariance from './../../base/snanvariance';
setReadOnly( ns, 'snanvariance', snanvariance );

/**
* @name snanvariancech
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/snanvariancech}
*/
import snanvariancech from './../../base/snanvariancech';
setReadOnly( ns, 'snanvariancech', snanvariancech );

/**
* @name snanvariancepn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/snanvariancepn}
*/
import snanvariancepn from './../../base/snanvariancepn';
setReadOnly( ns, 'snanvariancepn', snanvariancepn );

/**
* @name snanvariancetk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/snanvariancetk}
*/
import snanvariancetk from './../../base/snanvariancetk';
setReadOnly( ns, 'snanvariancetk', snanvariancetk );

/**
* @name snanvariancewd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/snanvariancewd}
*/
import snanvariancewd from './../../base/snanvariancewd';
setReadOnly( ns, 'snanvariancewd', snanvariancewd );

/**
* @name snanvarianceyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/snanvarianceyc}
*/
import snanvarianceyc from './../../base/snanvarianceyc';
setReadOnly( ns, 'snanvarianceyc', snanvarianceyc );


// EXPORTS //

export default ns;
